/**
 * fx_textures — the handful of procedural sprite textures every living-events
 * stage shares (one canvas each, cached for the page's life) and the SHARED
 * sprite materials built on them.
 *
 * MATERIAL-IDENTITY LAW (the street-lamp lesson, live-traced 2026-07-31): on
 * the node renderer every NEW material instance is a new pipeline build, and
 * every change to the scene's light set rebuilds EVERY lit material's graph
 * synchronously (7-12 s on real hardware, minutes headless). So:
 *   - stages never add or remove lights — glow is additive sprites only;
 *   - sprites share ONE material per (texture, colour, blending, opacity) —
 *     `spriteOf` hands out cached materials (`userData.shared`), and the
 *     animations drive SCALE, never per-sprite material opacity;
 *   - `disposeSprite` / `disposeGroup` leave shared materials alone.
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { disposeLater } from '../../lib/gpu_dispose.js';

let glow: THREE.CanvasTexture | null = null;
let ring: THREE.CanvasTexture | null = null;
let flag: THREE.CanvasTexture | null = null;
const materials = new Map<string, THREE.SpriteMaterial>();

/** Soft radial glow (lanterns, flames, sparks, incense). */
export function glowTexture(): THREE.CanvasTexture {
  if (glow) return glow;
  const cv = document.createElement('canvas');
  cv.width = cv.height = 64;
  const g = cv.getContext('2d')!;
  const grd = g.createRadialGradient(32, 32, 2, 32, 32, 32);
  grd.addColorStop(0, 'rgba(255,255,255,1)');
  grd.addColorStop(0.35, 'rgba(255,255,255,0.55)');
  grd.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grd;
  g.fillRect(0, 0, 64, 64);
  glow = new THREE.CanvasTexture(cv);
  return glow;
}

/** Thin ring (shockwaves, shrine halos). */
export function ringTexture(): THREE.CanvasTexture {
  if (ring) return ring;
  const cv = document.createElement('canvas');
  cv.width = cv.height = 128;
  const g = cv.getContext('2d')!;
  g.strokeStyle = 'rgba(255,255,255,1)';
  g.lineWidth = 7;
  g.beginPath();
  g.arc(64, 64, 54, 0, Math.PI * 2);
  g.stroke();
  ring = new THREE.CanvasTexture(cv);
  return ring;
}

/** A pennant triangle (bunting). */
export function flagTexture(): THREE.CanvasTexture {
  if (flag) return flag;
  const cv = document.createElement('canvas');
  cv.width = cv.height = 32;
  const g = cv.getContext('2d')!;
  g.fillStyle = '#fff';
  g.beginPath(); g.moveTo(2, 2); g.lineTo(30, 2); g.lineTo(16, 30); g.closePath(); g.fill();
  flag = new THREE.CanvasTexture(cv);
  return flag;
}

export interface TextLine { text: string; font?: string; color?: string }

/** Redraw a text plate INTO an existing canvas (the battle standard redraws in place — never a new texture per round). */
export function drawTextPlate(cv: HTMLCanvasElement, lines: TextLine[], bg = 'rgba(12,10,18,0.72)', stroke = 'rgba(255,255,255,0.25)'): void {
  const w = cv.width, h = cv.height;
  const g = cv.getContext('2d')!;
  g.clearRect(0, 0, w, h);
  g.fillStyle = bg;
  g.beginPath();
  const r = 18;
  g.moveTo(r, 0); g.lineTo(w - r, 0); g.quadraticCurveTo(w, 0, w, r); g.lineTo(w, h - r); g.quadraticCurveTo(w, h, w - r, h);
  g.lineTo(r, h); g.quadraticCurveTo(0, h, 0, h - r); g.lineTo(0, r); g.quadraticCurveTo(0, 0, r, 0); g.closePath();
  g.fill();
  g.strokeStyle = stroke; g.lineWidth = 3; g.stroke();
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  const step = h / (lines.length + 1);
  lines.forEach((l, i) => {
    g.font = l.font ?? '600 30px system-ui';
    g.fillStyle = l.color ?? '#f3ecff';
    g.fillText(l.text, w / 2, step * (i + 1));
  });
}

/** Multi-line text on a translucent plate (banners, bubbles). UNIQUE per call — use sparingly; dispose via disposeLater. */
export function textTexture(lines: TextLine[], w = 512, h = 160, bg = 'rgba(12,10,18,0.72)', stroke = 'rgba(255,255,255,0.25)'): THREE.CanvasTexture {
  const cv = document.createElement('canvas');
  cv.width = w; cv.height = h;
  drawTextPlate(cv, lines, bg, stroke);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** The shared material for a (texture, colour, blending, opacity) — built once, never disposed. */
export function sharedMaterial(map: THREE.Texture, color: number, additive = true, opacity = 1): THREE.SpriteMaterial {
  const key = `${map.uuid}|${color}|${additive ? 'a' : 'n'}|${Math.round(opacity * 100)}`;
  let m = materials.get(key);
  if (!m) {
    m = new THREE.SpriteMaterial({
      map, color, transparent: true, depthWrite: false, opacity,
      blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
    });
    m.userData.shared = true;
    markMrtOverlay(m); // fx sprites over the world leave the G-buffer alone (AO)
    materials.set(key, m);
  }
  return m;
}

/** A sprite on a SHARED material. Animate its scale/position — never its material. */
export function spriteOf(map: THREE.Texture, color: number, size: number, additive = true, opacity = 1): THREE.Sprite {
  const sp = new THREE.Sprite(sharedMaterial(map, color, additive, opacity));
  sp.scale.setScalar(size);
  return sp;
}

export function isShared(m: THREE.Material | undefined | null): boolean {
  return !!m && m.userData.shared === true;
}

export function disposeSprite(sp: THREE.Sprite): void {
  sp.parent?.remove(sp);
  const m = sp.material as THREE.SpriteMaterial;
  if (isShared(m)) return;
  // Deferred (the warp-teardown law): the last frame that drew this sprite may still be in flight.
  if (m.map && m.map !== glow && m.map !== ring && m.map !== flag) disposeLater(m.map);
  disposeLater(m);
}

/** How many shared materials exist (probe surface). */
export function sharedMaterialCount(): number { return materials.size; }
